import iconHash from '@phosphor-icons/core/regular/hash.svg';
import iconPencilSimple from '@phosphor-icons/core/regular/pencil-simple.svg';
import iconPushPin from '@phosphor-icons/core/regular/push-pin.svg';
import iconRepeat from '@phosphor-icons/core/regular/repeat.svg';
import iconRocketLaunch from '@phosphor-icons/core/regular/rocket-launch.svg';
import iconUsersThree from '@phosphor-icons/core/regular/users-three.svg';
import { Link, linkOptions, useNavigate, useRouter } from '@tanstack/react-router';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  defineMessages,
  useIntl,
  FormattedDate,
  FormattedList,
  FormattedMessage,
} from 'react-intl';

import AccountContainer from '@/components/accounts/account-container';
import { Hotkeys } from '@/components/hotkeys';
import StatusTypeIcon from '@/components/statuses/status-type-icon';
import Card from '@/components/ui/card';
import Icon from '@/components/ui/icon';
import { deckColumnRouterRegistry, useColumnId } from '@/contexts/deck-column-id-context';
import Emojify from '@/emoji/emojify';
import { useLoggedIn } from '@/hooks/use-logged-in';
import { useReblog } from '@/hooks/use-reblog';
import { useScopeUrl } from '@/hooks/use-scope-url';
import { useStatusUnavailable } from '@/hooks/use-status-availability';
import { useGroupQuery } from '@/queries/groups/use-group';
import { useFollowedTags } from '@/queries/hashtags/use-followed-tags';
import { useStatus, type SelectedStatus } from '@/queries/statuses/use-status';
import {
  useFavouriteStatus,
  useUnfavouriteStatus,
} from '@/queries/statuses/use-status-interactions';
import { useComposeActions } from '@/stores/compose';
import { useModalsActions } from '@/stores/modals';
import { usePluraldawnMatch } from '@/stores/pluraldawn';
import { useSettings } from '@/stores/settings';
import { useStatusMeta, useStatusMetaActions } from '@/stores/status-meta';
import { textForScreenReader } from '@/utils/status';

import { AccountLink } from '../accounts/account-link';
import HashtagLink from '../hashtag-link';
import RelativeTimestamp from '../relative-timestamp';

import StatusActionBar from './action-bar';
import EventPreview from './events/event-preview';
import RssFeedInfo from './rss-feed-info';
import StatusContent from './status-content';
import StatusInfo from './status-info';
import StatusInteractionBar from './status-interaction-bar';
import StatusLanguagePicker from './status-language-picker';
import { StatusLink } from './status-link';
import StatusReactionsBar from './status-reactions-bar';
import StatusReplyMentions from './status-reply-mentions';
import Tombstone from './tombstone';

import type { FilterContextType } from '@/queries/settings/use-filters';
import type { FilterResult } from 'pl-api';

const messages = defineMessages({
  applicationName: { id: 'status.application_name', defaultMessage: 'Sent from {name}' },
  edited: { id: 'status.edited', defaultMessage: 'Edited {date}' },
  rebloggedBy: { id: 'status.reblogged_by', defaultMessage: '{name} reposted' },
});

interface IAccountInfo {
  status: SelectedStatus;
  columnId?: string;
}

const AccountInfo: React.FC<IAccountInfo> = React.memo(({ status, columnId }) => {
  const intl = useIntl();
  const { statusActionBarItems } = useSettings();

  return (
    <div className='status__account-info'>
      {!statusActionBarItems.length && <StatusActionBar status={status} fromBookmarks expandable />}
      <StatusLink
        status={status}
        account={status.account}
        className='status__timestamp-link'
        columnId={columnId}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <RelativeTimestamp timestamp={status.created_at} className='status__timestamp' />
      </StatusLink>
      <StatusTypeIcon visibility={status.visibility} />
      <StatusLanguagePicker status={status} />
      {!!status.edited_at && (
        <>
          <span className='separator' />

          <Icon
            className='status__edited-icon'
            src={iconPencilSimple}
            title={intl.formatMessage(messages.edited, {
              date: intl.formatDate(new Date(status.edited_at), {
                hour12: true,
                month: 'short',
                day: '2-digit',
                hour: 'numeric',
                minute: '2-digit',
              }),
            })}
          />
        </>
      )}
    </div>
  );
});

AccountInfo.displayName = 'AccountInfo';

interface IStatusMeta {
  status: SelectedStatus;
}

const StatusMeta: React.FC<IStatusMeta> = ({ status }) => {
  const intl = useIntl();
  const { openModal } = useModalsActions();

  const handleOpenCompareHistoryModal = () => {
    openModal('COMPARE_HISTORY', { statusId: status.id });
  };

  return (
    <div className='status__meta'>
      <StatusInteractionBar status={status} />

      <div className='status__meta__end'>
        <div className='status__meta__info'>
          <a
            href={status.url}
            target='_blank'
            rel='noopener noreferrer'
            className='status__meta__link'
          >
            <FormattedDate
              value={new Date(status.created_at)}
              hour12
              year='numeric'
              month='short'
              day='2-digit'
              hour='numeric'
              minute='2-digit'
            />
          </a>

          {status.application && (
            <>
              <span className='separator' />
              <a
                href={status.application.website ?? '#'}
                target='_blank'
                rel='noopener noreferrer'
                className='status__meta__link'
                title={intl.formatMessage(messages.applicationName, {
                  name: status.application.name,
                })}
              >
                {status.application.name}
              </a>
            </>
          )}

          {status.edited_at && (
            <>
              <span className='separator' />
              <button className='status__history' onClick={handleOpenCompareHistoryModal}>
                <FormattedMessage
                  id='status.edited'
                  defaultMessage='Edited {date}'
                  values={{
                    date: intl.formatDate(new Date(status.edited_at), {
                      hour12: true,
                      month: 'short',
                      day: '2-digit',
                      hour: 'numeric',
                      minute: '2-digit',
                    }),
                  }}
                />
              </button>
            </>
          )}
        </div>

        <StatusTypeIcon visibility={status.visibility} />

        <StatusLanguagePicker status={status} showLabel />
      </div>
    </div>
  );
};

interface IStatusFollowedTagInfo {
  className?: string;
  status: SelectedStatus;
  avatarSize: number;
}

const StatusFollowedTagInfo: React.FC<IStatusFollowedTagInfo> = ({
  className,
  status,
  avatarSize,
}) => {
  const { data: followedTags } = useFollowedTags();

  const filteredTags = status.tags.filter((tag) =>
    followedTags?.some((followed) => followed.name.toLowerCase() === tag.name.toLowerCase()),
  );

  if (!filteredTags.length) {
    return null;
  }

  const tagLinks = filteredTags
    .slice(0, 2)
    .map((tag) => <HashtagLink key={tag.name} hashtag={tag.name} />);

  if (filteredTags.length > 2) {
    tagLinks.push(
      <FormattedMessage
        key='more'
        id='reply_mentions.more'
        defaultMessage='{count} more'
        values={{ count: filteredTags.length - 2 }}
      />,
    );
  }

  return (
    <StatusInfo
      className={className}
      avatarSize={avatarSize}
      icon={<Icon src={iconHash} className='status-info__icon--tag' aria-hidden />}
      text={
        <FormattedMessage
          id='status.followed_tag'
          defaultMessage='You’re following {tags}'
          values={{
            tags: <FormattedList type='conjunction' value={tagLinks} />,
          }}
        />
      }
    />
  );
};

interface IStatus {
  id?: string;
  avatarSize?: number;
  status: SelectedStatus;
  onClick?: () => void;
  muted?: boolean;
  contextType?: FilterContextType;
  unread?: boolean;
  onMoveUp?: (statusId: string, featured?: boolean) => void;
  onMoveDown?: (statusId: string, featured?: boolean) => void;
  focusable?: boolean;
  featured?: boolean;
  hideActionBar?: boolean;
  hoverable?: boolean;
  variant?: 'default' | 'rounded' | 'slim';
  showGroup?: boolean;
  showInfo?: boolean;
  fromBookmarks?: boolean;
  className?: string;
  expandable?: boolean;
  columnId?: string;
  detailed?: boolean;
  withMedia?: boolean;
}

const Status: React.FC<IStatus> = React.memo((props) => {
  const {
    status,
    avatarSize = 42,
    focusable = true,
    hoverable = true,
    onClick,
    onMoveUp,
    onMoveDown,
    muted,
    featured,
    unread,
    hideActionBar,
    variant = 'rounded',
    showGroup = true,
    showInfo = true,
    fromBookmarks = false,
    className,
    contextType,
    expandable = true,
    detailed = false,
    withMedia = true,
  } = props;

  const intl = useIntl();
  const navigate = useNavigate();
  const router = useRouter();
  const scopeUrl = useScopeUrl();

  const {
    expandStatuses,
    collapseStatuses,
    expandStatusSpoilers,
    collapseStatusSpoilers,
    toggleStatusesMediaHidden,
    unfilterStatus,
  } = useStatusMetaActions();
  const { spoilerExpanded, deleted, showFiltered } = useStatusMeta(status.id);
  const { openModal } = useModalsActions();
  const { replyCompose, mentionCompose } = useComposeActions();
  const columnId = useColumnId() || props.columnId;
  const { showFilteredStatusAuthor, statusActionBarItems, useRocketIconForReblogs } = useSettings();
  const didShowCard = useRef(false);
  const node = useRef<HTMLDivElement>(null);
  const { me } = useLoggedIn();
  const pluraldawnMatch = usePluraldawnMatch(status.id);

  const actualStatus =
    useStatus(status.reblog_id || undefined, { withFilteredResults: true }).data || status;

  const unavailable = useStatusUnavailable(status.id);
  const actualUnavailable = useStatusUnavailable(actualStatus.id);

  const { data: group } = useGroupQuery(actualStatus.group_id ?? undefined);

  const { mutate: favouriteStatus } = useFavouriteStatus(actualStatus.id);
  const { mutate: unfavouriteStatus } = useUnfavouriteStatus(actualStatus.id);
  const reblog = useReblog(actualStatus);

  const isReblog = status.reblog_id;

  const filterResults = useMemo(() => {
    if (!contextType) return [];

    return [...status.filtered, ...actualStatus.filtered]
      .filter(
        ({ filter }) => filter.filter_action === 'warn' && filter.context.includes(contextType),
      )
      .reduce<Array<FilterResult>>((uniqueFilters, current) => {
        if (
          !uniqueFilters.some(({ filter: uniqueFilter }) => uniqueFilter.id === current.filter.id)
        ) {
          uniqueFilters.push(current);
        }
        return uniqueFilters;
      }, []);
  }, [status.filtered, actualStatus.filtered, contextType]);
  const filtered = filterResults.length > 0;

  // Track height changes we know about to compensate scrolling.
  useEffect(() => {
    didShowCard.current = Boolean(!muted && status?.card);
  }, []);

  const handleClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();

    // If the user is selecting text, don't focus the status.
    if (getSelection()?.toString().length) {
      return;
    }

    const link = linkOptions({
      to: '/@{-$username}/posts/$statusId',
      params: { username: actualStatus.account.acct, statusId: actualStatus.id },
    });

    if (!e || !(e.ctrlKey || e.metaKey)) {
      if (onClick) {
        onClick();
      } else if (columnId) {
        deckColumnRouterRegistry.get(columnId)?.router.navigate(link);
      } else {
        navigate(link);
      }
    } else {
      const url = router.buildLocation(link).href;
      window.open(url, '_blank');
    }
  };

  const handleHotkeyOpenMedia = (e?: KeyboardEvent) => {
    const status = actualStatus;

    e?.preventDefault();

    if (status.media_attachments.length > 0) {
      openModal('MEDIA', { statusId: status.id, media: status.media_attachments, index: 0 });
    }
  };

  const handleHotkeyReply = (e?: KeyboardEvent) => {
    if (status.rss_feed) return;

    e?.preventDefault();
    replyCompose(actualStatus, scopeUrl, columnId, status.reblog_id ? status.account : undefined);
  };

  const handleHotkeyFavourite = (e?: KeyboardEvent) => {
    if (status.rss_feed) return;

    e?.preventDefault();
    if (status.favourited) unfavouriteStatus();
    else favouriteStatus();
  };

  const handleHotkeyBoost = (e?: KeyboardEvent) => {
    if (status.rss_feed) return;

    reblog({ event: e });
  };

  const handleHotkeyMention = (e?: KeyboardEvent) => {
    if (status.rss_feed) return;

    e?.preventDefault();
    mentionCompose(actualStatus.account, scopeUrl, columnId);
  };

  const handleHotkeyOpen = () => {
    const link = linkOptions({
      to: '/@{-$username}/posts/$statusId',
      params: { username: actualStatus.account.acct, statusId: actualStatus.id },
    });
    if (columnId) {
      deckColumnRouterRegistry.get(columnId)?.router.navigate(link);
    } else {
      navigate(link);
    }
  };

  const handleHotkeyOpenProfile = () => {
    const link = linkOptions({
      to: '/@{$username}',
      params: { username: actualStatus.account.acct },
    });
    if (columnId) {
      deckColumnRouterRegistry.get(columnId)?.router.navigate(link);
    } else {
      navigate(link);
    }
  };

  const handleHotkeyMoveUp = () => {
    if (onMoveUp) {
      return onMoveUp(status.id, featured);
    }
    return false;
  };

  const handleHotkeyMoveDown = () => {
    if (onMoveDown) {
      return onMoveDown(status.id, featured);
    }
    return false;
  };

  const handleHotkeyToggleSensitive = () => {
    toggleStatusesMediaHidden([actualStatus.id]);
  };

  const handleHotkeyToggleHidden = () => {
    if (!spoilerExpanded) {
      expandStatusSpoilers([status.id]);
      expandStatuses([status.id]);
    } else {
      collapseStatusSpoilers([status.id]);
      collapseStatuses([status.id]);
    }
  };

  const handleHotkeyReact = () => {
    if (status.rss_feed) return;

    (node.current?.querySelector('.emoji-picker-dropdown') as HTMLButtonElement)?.click();
  };

  const handleUnfilter = () => {
    unfilterStatus(actualStatus.id);
    if (actualStatus.id !== status.id) unfilterStatus(status.id);
  };

  const statusInfo = useMemo(() => {
    if (!showInfo) return null;

    if (isReblog && showGroup && group) {
      return (
        <StatusInfo
          className='status-info--header'
          avatarSize={avatarSize}
          icon={
            <Icon
              src={useRocketIconForReblogs ? iconRocketLaunch : iconRepeat}
              className='status-info__icon--reblog'
              aria-hidden
            />
          }
          text={
            <FormattedMessage
              id='status.reblogged_by_with_group'
              defaultMessage='{name} reposted from {group}'
              values={{
                name: (
                  <AccountLink
                    account={status.account}
                    className='status-info__link'
                    columnId={columnId}
                  >
                    <bdi>
                      <strong>
                        <Emojify
                          text={status.account.display_name}
                          emojis={status.account.emojis}
                        />
                      </strong>
                    </bdi>
                  </AccountLink>
                ),
                group: (
                  <Link
                    to='/groups/$groupId'
                    params={{ groupId: group.id }}
                    className='status-info__link'
                  >
                    <strong>
                      <Emojify text={group.display_name} emojis={group.emojis} />
                    </strong>
                  </Link>
                ),
              }}
            />
          }
        />
      );
    } else if (isReblog) {
      const values = {
        name: (
          <AccountLink
            account={status.account}
            className='status-info__link'
            key={status.account.acct}
            columnId={columnId}
          >
            <bdi>
              <strong>
                <Emojify text={status.account.display_name} emojis={status.account.emojis} />
              </strong>
            </bdi>
          </AccountLink>
        ),
      };

      return (
        <StatusInfo
          className='status-info--header'
          avatarSize={avatarSize}
          icon={
            <Icon
              src={useRocketIconForReblogs ? iconRocketLaunch : iconRepeat}
              className='status-info__icon--reblog'
              aria-hidden
            />
          }
          text={
            status.visibility === 'private' ? (
              <FormattedMessage
                id='status.reblogged_by_private'
                defaultMessage='{name} reposted to followers'
                values={values}
              />
            ) : (
              <FormattedMessage
                id='status.reblogged_by'
                defaultMessage='{name} reposted'
                values={values}
              />
            )
          }
        />
      );
    } else if (featured) {
      return (
        <StatusInfo
          className='status-info--header'
          avatarSize={avatarSize}
          icon={<Icon src={iconPushPin} className='status-info__icon--pinned' aria-hidden />}
          text={<FormattedMessage id='status.pinned' defaultMessage='Pinned post' />}
        />
      );
    } else if (showGroup && group) {
      return (
        <StatusInfo
          className='status-info--header'
          avatarSize={avatarSize}
          icon={<Icon src={iconUsersThree} className='status-info__icon--group' aria-hidden />}
          text={
            <FormattedMessage
              id='status.group'
              defaultMessage='Posted in {group}'
              values={{
                group: (
                  <Link
                    to='/groups/$groupId'
                    params={{ groupId: group.id }}
                    className='status-info__link'
                  >
                    <bdi>
                      <strong>
                        <Emojify text={group.display_name} emojis={group.emojis} />
                      </strong>
                    </bdi>
                  </Link>
                ),
              }}
            />
          }
        />
      );
    }
  }, [status.account, group?.id, columnId]);

  if (!status) return null;

  if (deleted || unavailable || actualUnavailable)
    return (
      <Tombstone
        id={actualUnavailable ? actualStatus.id : status.id}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        deleted={deleted}
      />
    );

  if (filtered && showFiltered !== true) {
    const body = (
      <Card variant={variant} className='status__wrapper status__wrapper--filtered' ref={node}>
        {showFilteredStatusAuthor && actualStatus.account_id && (
          <div className='status__account'>
            <AccountContainer
              key={actualStatus.account_id}
              id={actualStatus.account_id}
              action={<AccountInfo status={actualStatus} columnId={columnId} />}
              showAccountHoverCard={hoverable}
              withLinkToProfile={hoverable}
              approvalStatus={actualStatus.approval_status}
              avatarSize={avatarSize}
              actionAlignment='top'
              withLocked={false}
              pluraldawnMatch={pluraldawnMatch}
            />
          </div>
        )}
        <p className='status__filtered'>
          {actualStatus.account_id === me ? (
            <FormattedMessage id='status.filtered.self' defaultMessage='Filtered (your own post)' />
          ) : (
            <FormattedMessage id='status.filtered' defaultMessage='Filtered' />
          )}
          : {filterResults.map(({ filter }) => filter.title).join(', ')}.{' '}
          <button className='status__filtered-button' onClick={handleUnfilter}>
            <FormattedMessage id='status.show_filter_reason' defaultMessage='Show anyway' />
          </button>
        </p>
      </Card>
    );

    if (muted) return body;

    const minHandlers = {
      moveUp: handleHotkeyMoveUp,
      moveDown: handleHotkeyMoveDown,
    };

    return (
      <Hotkeys
        handlers={minHandlers}
        focusable={focusable}
        element='article'
        lang={actualStatus.language || undefined}
        data-status-id={status.id}
      >
        {body}
      </Hotkeys>
    );
  }

  let rebloggedByText;
  if (isReblog) {
    rebloggedByText = intl.formatMessage(messages.rebloggedBy, { name: status.account.acct });
  }

  const body = (
    <div
      className={clsx('status', {
        'status--reply': !!status.in_reply_to_id,
        'status--detailed': detailed,
      })}
      data-featured={featured ? 'true' : null}
      data-visibility={actualStatus.visibility}
      data-id={status.id}
      aria-label={textForScreenReader(intl, actualStatus, rebloggedByText, scopeUrl)}
      ref={node}
      {...(detailed ? {} : { onClick: handleClick, role: 'link' })}
    >
      <Card
        variant={variant}
        className={clsx('status__wrapper', className, {
          'status__wrapper--rounded': variant === 'rounded',
          'status__wrapper--muted': muted,
          'status__wrapper--read': unread === false,
        })}
      >
        {statusInfo}

        {status.rss_feed ? (
          <RssFeedInfo feed={status.rss_feed} timestamp={status.created_at} url={status.url} />
        ) : (
          actualStatus.account_id && (
            <div className='status__account'>
              <AccountContainer
                key={actualStatus.account_id}
                id={actualStatus.account_id}
                action={
                  detailed ? (
                    statusActionBarItems.length === 0 ? (
                      <div className='status__account-actions'>
                        <StatusActionBar
                          status={actualStatus}
                          rebloggedBy={isReblog ? status.account : undefined}
                          fromBookmarks={fromBookmarks}
                        />
                      </div>
                    ) : undefined
                  ) : (
                    <AccountInfo status={actualStatus} columnId={columnId} />
                  )
                }
                hideActions={detailed}
                showAccountHoverCard={hoverable}
                withLinkToProfile={hoverable}
                approvalStatus={actualStatus.approval_status}
                avatarSize={avatarSize}
                actionAlignment={detailed ? 'center' : 'top'}
                withLocked={false}
                pluraldawnMatch={pluraldawnMatch}
              />
            </div>
          )
        )}

        <div className='status__content-wrapper'>
          <StatusReplyMentions status={actualStatus} hoverable={hoverable && !detailed} />

          {actualStatus.event && !detailed ? (
            <EventPreview status={actualStatus} />
          ) : (
            <StatusContent
              status={actualStatus}
              onClick={detailed ? undefined : handleClick}
              collapsable={!detailed}
              translatable
              withMedia={withMedia}
              textSize={detailed ? 'lg' : 'md'}
              expandable={detailed || expandable}
              contextType={contextType}
            />
          )}

          {!status.rss_feed && !hideActionBar && (
            <>
              <StatusReactionsBar status={actualStatus} collapsed={!detailed} />

              {detailed && <StatusMeta status={actualStatus} />}

              {statusActionBarItems.length > 0 && (
                <div
                  className={clsx('status__actions', {
                    'status__actions--reacted': actualStatus.emoji_reactions.length,
                  })}
                >
                  <StatusActionBar
                    status={actualStatus}
                    rebloggedBy={isReblog ? status.account : undefined}
                    fromBookmarks={fromBookmarks}
                    expandable={!detailed}
                    space={detailed ? 'lg' : 'sm'}
                    withLabels={detailed}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );

  if (muted) return body;

  const handlers = {
    reply: handleHotkeyReply,
    favourite: handleHotkeyFavourite,
    boost: handleHotkeyBoost,
    mention: handleHotkeyMention,
    open: handleHotkeyOpen,
    openProfile: handleHotkeyOpenProfile,
    moveUp: handleHotkeyMoveUp,
    moveDown: handleHotkeyMoveDown,
    toggleSensitive: handleHotkeyToggleSensitive,
    toggleHidden: handleHotkeyToggleHidden,
    openMedia: handleHotkeyOpenMedia,
    react: handleHotkeyReact,
  };

  return (
    <Hotkeys
      handlers={handlers}
      focusable={focusable}
      element='article'
      lang={actualStatus.language || undefined}
      data-testid='status'
      data-status-id={status.id}
    >
      {body}
    </Hotkeys>
  );
});

Status.displayName = 'Status';

export { type IStatus, Status as default, StatusFollowedTagInfo };
